import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { ClaudeMdEditor } from "./ClaudeMdEditor.js";
import { WebhookDialog } from "./WebhookDialog.js";
import { WorkspaceSettingsDialog } from "./WorkspaceSettingsDialog.js";
export const WORKSPACE_SETTINGS_TABS = [
    { id: "profile", label: "Profile" },
    { id: "integrations", label: "Integrations" },
    { id: "agent-defaults", label: "Agent defaults" },
];
/**
 * Sub-rail entries for each tab. The `id` is the DOM id of the corresponding
 * `<section>` in the scrollable body — clicks scroll-into-view that anchor,
 * and an IntersectionObserver flips the highlighted entry as the user scrolls.
 *
 * Integrations order matches Claude Design's bundle (OpenRouter first, since
 * everything else funnels through that key, then Search, then MCP, then the
 * inbound Webhook routes last).
 */
const TAB_SECTIONS = {
    profile: [
        { id: "wsp-identity", label: "Identity" },
        { id: "wsp-icon", label: "Workspace icon" },
    ],
    integrations: [
        { id: "wsp-openrouter", label: "OpenRouter" },
        { id: "wsp-search", label: "Search providers" },
        { id: "wsp-mcp", label: "MCP servers" },
        { id: "wsp-webhooks", label: "Webhook routes" },
    ],
    "agent-defaults": [
        { id: "wsp-agent-prompt", label: "System prompt" },
        // KTK-395 — sits next to the system prompt because they're the same kind
        // of thing to the reader: instructions every agent here starts with.
        { id: "wsp-claude-md", label: "CLAUDE.md" },
        { id: "wsp-spend", label: "Spend" },
    ],
};
/**
 * Full-page workspace settings view. Replaces the channel/agent content area
 * when the user clicks the "Workspace settings" item in the workspace-named
 * sidebar collapsible. Layout (KTK-275 redesign):
 *
 *   ┌───────────────────────────────────────────────────────────────────┐
 *   │ <workspace name>  workspace settings   [Profile][Knowledge]…      │
 *   ├──────────┬────────────────────────────────────────────────────────┤
 *   │ • dot    │ scrollable body — sections stacked, anchored by id;    │
 *   │   sub    │ sub-rail scroll-spy via IntersectionObserver.          │
 *   │   nav    │                                                        │
 *   └──────────┴────────────────────────────────────────────────────────┘
 *
 * The actual section bodies are the existing dialogs rendered in `inline`
 * mode (no modal chrome). Each tab change re-mounts the relevant dialogs;
 * dialogs are unmounted when their tab is inactive so we don't pay the data-
 * fetch cost for tabs the user isn't looking at.
 */
export function WorkspaceSettingsPage({ workspaceId, workspaceName, activeTab, onTabChange, onHandleChange, onIconChange, }) {
    const sections = TAB_SECTIONS[activeTab];
    // Every entry in TAB_SECTIONS has at least one section by construction —
    // the `!` keeps TS happy without bringing in a runtime guard.
    const firstSectionId = sections[0].id;
    const scrollRef = useRef(null);
    const [activeSection, setActiveSection] = useState(firstSectionId);
    // Reset the active sub-rail entry to the first section whenever the tab
    // changes — otherwise the highlight lingers on whatever was last in view
    // in the previous tab.
    useEffect(() => {
        setActiveSection(firstSectionId);
    }, [activeTab, firstSectionId]);
    // Scroll-spy: highlight the sub-rail entry whose section is closest to the
    // top of the scroll container. We use a rootMargin that biases the trigger
    // line toward the top — feels more natural than the geometric centre.
    useEffect(() => {
        const root = scrollRef.current;
        if (!root)
            return;
        const observer = new IntersectionObserver((entries) => {
            const visible = entries
                .filter((e) => e.isIntersecting)
                .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
            if (visible[0])
                setActiveSection(visible[0].target.id);
        }, {
            root,
            // Fire when the section's top crosses the top 30% of the viewport.
            // Bottom margin keeps the last section selectable even when it's
            // shorter than the viewport.
            rootMargin: "0px 0px -70% 0px",
            threshold: 0,
        });
        sections.forEach(({ id }) => {
            const el = root.querySelector(`#${id}`);
            if (el)
                observer.observe(el);
        });
        return () => observer.disconnect();
    }, [activeTab, sections]);
    const scrollToSection = (id) => {
        const root = scrollRef.current;
        if (!root)
            return;
        const el = root.querySelector(`#${id}`);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            setActiveSection(id);
        }
    };
    const tabBody = useMemo(() => {
        switch (activeTab) {
            case "profile":
                return (_jsxs(_Fragment, { children: [_jsx(SectionAnchor, { id: "wsp-identity", children: _jsx(WorkspaceSettingsDialog, { workspaceId: workspaceId, inline: true, showSections: ["identity"], onClose: () => { }, onHandleChange: onHandleChange, onIconChange: onIconChange }) }), _jsx(SectionAnchor, { id: "wsp-icon", children: _jsx(WorkspaceSettingsDialog, { workspaceId: workspaceId, inline: true, showSections: ["icon"], onClose: () => { }, onHandleChange: onHandleChange, onIconChange: onIconChange }) })] }));
            case "integrations":
                return (_jsxs(_Fragment, { children: [_jsx(SectionAnchor, { id: "wsp-openrouter", children: _jsx(WorkspaceSettingsDialog, { workspaceId: workspaceId, inline: true, showSections: ["openrouter"], onClose: () => { }, onHandleChange: onHandleChange, onIconChange: onIconChange }) }), _jsx(SectionAnchor, { id: "wsp-search", children: _jsx(WorkspaceSettingsDialog, { workspaceId: workspaceId, inline: true, showSections: ["search"], onClose: () => { }, onHandleChange: onHandleChange, onIconChange: onIconChange }) }), _jsx(SectionAnchor, { id: "wsp-mcp", children: _jsx(WorkspaceSettingsDialog, { workspaceId: workspaceId, inline: true, showSections: ["mcp"], onClose: () => { }, onHandleChange: onHandleChange, onIconChange: onIconChange }) }), _jsx(SectionAnchor, { id: "wsp-webhooks", children: _jsx(WebhookDialog, { workspaceId: workspaceId, inline: true, onClose: () => { } }) })] }));
            case "agent-defaults":
                return (_jsxs(_Fragment, { children: [_jsx(SectionAnchor, { id: "wsp-agent-prompt", children: _jsx(WorkspaceSettingsDialog, { workspaceId: workspaceId, inline: true, showSections: ["agent-prompt"], onClose: () => { }, onHandleChange: onHandleChange, onIconChange: onIconChange }) }), _jsx(SectionAnchor, { id: "wsp-claude-md", children: _jsx(ClaudeMdEditor, { scope: { kind: "workspace", workspaceId } }) }), _jsx(SectionAnchor, { id: "wsp-spend", children: _jsx(WorkspaceSettingsDialog, { workspaceId: workspaceId, inline: true, showSections: ["spend"], onClose: () => { }, onHandleChange: onHandleChange, onIconChange: onIconChange }) })] }));
        }
    }, [activeTab, workspaceId, onHandleChange, onIconChange]);
    return (_jsxs("div", { className: "ws-settings", children: [_jsxs("div", { className: "ws-settings__tabs", children: [_jsxs("div", { className: "ws-settings__tabs-l", children: [_jsx("h1", { className: "ws-settings__title", children: workspaceName }), _jsx("span", { className: "ws-settings__crumb", children: "workspace settings" })] }), _jsx("div", { className: "ws-settings__tabs-r", children: WORKSPACE_SETTINGS_TABS.map((tab) => {
                            const active = tab.id === activeTab;
                            return (_jsx("button", { type: "button", onClick: () => onTabChange(tab.id), className: `ws-tab${active ? " is-active" : ""}`, children: tab.label }, tab.id));
                        }) })] }), _jsxs("div", { className: "ws-settings__row", children: [_jsx("nav", { "aria-label": "Workspace settings sub-sections", className: "ws-settings__rail", children: sections.map((section) => {
                            const active = section.id === activeSection;
                            return (_jsxs("button", { type: "button", onClick: () => scrollToSection(section.id), className: `ws-subnav${active ? " is-active" : ""}`, children: [_jsx("span", { className: "ws-subnav__dot" }), _jsx("span", { children: section.label })] }, section.id));
                        }) }), _jsx("div", { ref: scrollRef, className: "ws-settings__scroll", children: tabBody })] })] }));
}
/**
 * Wraps a tab's section so it has a stable DOM `id` for sub-rail anchoring +
 * IntersectionObserver. The `ws-sect` class is what the scroll-margin hangs
 * off — without it scroll-into-view lands the heading flush against the
 * sticky tab strip.
 */
function SectionAnchor({ id, children, }) {
    return (_jsx("section", { id: id, className: "ws-sect", children: children }));
}
//# sourceMappingURL=WorkspaceSettingsPage.js.map